import { useCallback, useMemo, useState } from 'react';
import RexDisconnectedModal from './RexDisconnectedModal';
import { RexDisconnectedModalFields, RexDisconnectedModalFieldValue } from './RexDataUpdateHelper';

export interface RexDisconnectedModalChange {
  field: RexDisconnectedModalFields;
  value: RexDisconnectedModalFieldValue;
}

export const useRexDisconnectedModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [changedFields, setChangedFields] = useState<
    Map<RexDisconnectedModalFields, RexDisconnectedModalFieldValue>
  >(new Map());

  const setDialogOpen = useCallback((open: boolean) => {
    setIsOpen(open);

    if (!open) {
      setChangedFields(new Map());
    }
  }, []);

  const addChangedField = useCallback(
    (field: RexDisconnectedModalFields, value: RexDisconnectedModalFieldValue) => {
      setChangedFields((prev) => {
        const next = new Map(prev);
        next.set(field, value);
        return next;
      });
    },
    []
  );

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const openWithChanges = useCallback((changes: RexDisconnectedModalChange[]) => {
    const next = new Map<RexDisconnectedModalFields, RexDisconnectedModalFieldValue>();

    changes.forEach(({ field, value }) => {
      next.set(field, value);
    });

    setChangedFields(next);

    const hasChanges = next.size > 0;
    setIsOpen(hasChanges);

    return hasChanges;
  }, []);

  const modal = useMemo(() => {
    if (!isOpen) return null;

    return <RexDisconnectedModal setIsDialogOpen={setDialogOpen} changedFields={changedFields} />;
  }, [changedFields, isOpen, setDialogOpen]);

  return {
    addChangedField,
    changedFields,
    isOpen,
    modal,
    openModal,
    openWithChanges,
    setChangedFields,
    setDialogOpen,
  };
};

export default useRexDisconnectedModal;
